import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { CreateProviderCommand, UpdateProviderCommand } from 'axiosApi/models';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { SCOPES } from '../../common/permissions';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { ButtonWithOptions } from 'components/common/ButtonWithOptions';
import RequiredField from 'components/common/RequiredField';
import intl from 'components/i18n/ReactIntlWrapper';
import { Address } from '../../axiosApi/models';
import AddressCompact from 'components/Address/AddressCompact';
import Spinner from 'components/Spinner';
import { ENTITY_TYPE } from 'common/constants';
import BillingTable from 'components/Billings/BillingTable';
import { Box, Button, Card, FormControl, MenuItem, Stack, TextField, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';

export type ProviderDetailInput = {
  name?: string | null;
  address: Address | null;
};

const ProviderDetail = () => {
  const api: Api = useApi();
  const navigate = useNavigate();
  const toast = useToastMessageQueue();
  const { id } = useParams();
  const idNumber = parseInt(id);
  const [addressId, setAddressId] = useState<number[]>([]);
  const [errors, setErrors] = useState({});
  const [disabled, setDisabled] = useState(false);
  const [disabledNew, setDisabledNew] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [input, setInput] = useState<ProviderDetailInput | null>({
    name: '',
    address: {},
  });

  const fetchProviderDetail = async (idNumber) => {
    try {
      const response = await api.employeeApi.apiVversionProviderIdGet(idNumber, '1');
      if (response.data) {
        setInput({
          name: response.data?.data?.name,
          address: response.data?.data?.address,
        });
        setAddressId([response.data?.data?.idAddress]);
      }
    } catch (error) {
      console.error('Fetch provider detail failed', error);
      toast.error({
        header: intl.get('providerDetail.toast.error.fetchProviderDetail'),
        body: error.message,
      });
    }
  };

  useEffect(() => {
    if (idNumber) {
      fetchProviderDetail(idNumber);
      setDisabled(true);
    }
    setDisabledNew(true);
  }, [idNumber]);

  const handleInputChange = function (e) {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
    setErrors(
      validate({
        ...input,
        [e.target.name]: e.target.value,
      })
    );
  };

  const validate = function (input: ProviderDetailInput) {
    setDisabledNew(false);
    let errors: any = {};
    if (!input.name) {
      errors.name = intl.get('validate.errors.isRequired');
    }
    return errors;
  };

  const handleSubmit = async (saveAndEdit: boolean = false) => {
    const errors = validate(input);
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      setLoading(true);
      if (idNumber) {
        try {
          let cmd: UpdateProviderCommand = {
            name: input.name,
            idAddress: input.address?.id,
            id: idNumber,
          };
          await api.employeeApi.apiVversionProviderPut('1', cmd, {});
          toast.success({
            body: intl.get('providerDetail.toast.success.handleSubmit'),
          });
          if (saveAndEdit) setDisabled(true);
          else navigate('/providers');
        } catch (error) {
          console.error('Update provider failed', error);
          toast.error({
            header: intl.get('providerDetail.toast.error.handleSubmit'),
            body: error.message,
          });
        } finally {
          setLoading(false);
        }
      } else {
        try {
          let cmd: CreateProviderCommand = {
            name: input.name,
          };
          const response = await api.employeeApi.apiVversionProviderPost('1', cmd, {});
          const newProvider = response?.data?.data;

          if (!newProvider?.id) {
            throw new Error('Missing provider ID in response.');
          }

          toast.success({
            message:
              intl.get('providerDetail.toast.success.handleSubmit.add') ||
              'Provider created successfully.',
          });

          if (saveAndEdit) navigate(`/provider/detail/${newProvider.id}`);
          else navigate('/providers');
        } catch (error) {
          toast.error({
            header: intl.get('providerDetail.toast.error.handleSubmit.add') || 'Error',
            message: error?.message ?? 'Provider save failed: unknown error',
          });
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleBackClick = () => {
    navigate('/providers');
  };

  const handleSaveClick = () => {
    handleSubmit(true);
  };

  const handleSaveCloseClick = () => {
    handleSubmit(false);
  };

  const handleEditMode = (e) => {
    e.preventDefault();
    if (idNumber) {
      fetchProviderDetail(idNumber);
    }
    setErrors({});
    setDisabled(!disabled);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {idNumber ? intl.get('providerDetail.header') : intl.get('newProvider.header')}
      </Typography>
      <Box>
        <FormControl fullWidth sx={{ maxWidth: 600 }}>
          <TextField
            id="providerName"
            name="name"
            label={intl.get('providerForm.name')}
            value={input?.name}
            onChange={handleInputChange}
            disabled={disabled}
          />
        </FormControl>
        <RequiredField />
        {!!idNumber && (
          <Stack gap={3} my={4}>
            <Card sx={{ p: 2 }}>
              <BillingTable
                entityId={idNumber}
                idEntityType={ENTITY_TYPE.PROVIDER}
                billToDisplay
                disabled={disabled}
              />
            </Card>
            <Card sx={{ p: 2 }}>
              <AddressCompact
                inputProvider={input}
                setInputProvider={setInput}
                allowOne={addressId.length > 0 && addressId[0] !== null}
                addressesId={addressId}
                setAddressesId={setAddressId}
                disabled={disabled}
                editScopes={[SCOPES['providers.edit']]}
                deleteScopes={[SCOPES['providers.edit']]}
              />
            </Card>
          </Stack>
        )}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          {!idNumber && !disabled ? (
            loading ? (
              <Spinner small={true} />
            ) : (
              <>
                <Button variant="outlined" onClick={handleBackClick}>
                  <ArrowBackIcon />
                </Button>
                <PermissionsGate
                  viewScopes={[SCOPES['providers.edit']]}
                  editScopes={[SCOPES['providers.edit']]}
                  editRoles={null}
                  viewRoles={null}
                >
                  <ButtonWithOptions
                    color="primary"
                    variant="contained"
                    disabled={Object.keys(errors).length > 0 || disabledNew}
                    menu={
                      <>
                        <MenuItem
                          onClick={handleSaveCloseClick}
                          disabled={Object.keys(errors).length > 0 || disabledNew}
                        >
                          {intl.get('save&close.button')}
                        </MenuItem>
                      </>
                    }
                    onClick={handleSaveClick}
                  >
                    <SaveIcon className="mb-1" /> {intl.get('save.button')}
                  </ButtonWithOptions>
                </PermissionsGate>
              </>
            )
          ) : null}
          {idNumber && disabled ? (
            <Box display={'flex'} gap={1}>
              <Button variant="outlined" onClick={handleBackClick}>
                <ArrowBackIcon />
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEditMode}
                startIcon={<EditIcon />}
              >
                {intl.get('edit.button')}
              </Button>
            </Box>
          ) : null}
          {idNumber && !disabled ? (
            loading ? (
              <Spinner small={true} />
            ) : (
              <Box display={'flex'} gap={1}>
                <Button variant="outlined" onClick={handleEditMode}>
                  {intl.get('cancel.button')}
                </Button>
                <PermissionsGate
                  viewScopes={[SCOPES['providers.edit']]}
                  editScopes={[SCOPES['providers.edit']]}
                  editRoles={null}
                  viewRoles={null}
                >
                  <ButtonWithOptions
                    color="primary"
                    variant="contained"
                    disabled={Object.keys(errors).length > 0 || disabled}
                    menu={
                      <>
                        <MenuItem
                          onClick={handleSaveCloseClick}
                          disabled={Object.keys(errors).length > 0 || disabled}
                        >
                          {intl.get('save&close.button')}
                        </MenuItem>
                      </>
                    }
                    onClick={handleSaveClick}
                  >
                    <SaveIcon className="mb-1" /> {intl.get('save.button')}
                  </ButtonWithOptions>
                </PermissionsGate>
              </Box>
            )
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default ProviderDetail;
